import { getNinjaConfig } from '@/config/ninja';
import { getLogger } from '@/logger';
import { Event } from '@/types/events';
import { loadScript } from '@/utils/browser';
import { makeMapping } from '@/utils/mapping';
import { normalizeEventValue } from '@/utils/string';
import { BaseTracker } from './BaseTracker';
import { initGa } from './init-snippets/initGa';

const logger = getLogger('GaTracker');

export class GaTracker extends BaseTracker {
  customDataLayer: string;

  constructor() {
    super();

    this.trackerName = 'ga';
    this.scriptTagId = 'ga-script-tag';
    this.scriptUrl = 'https://www.googletagmanager.com/gtag/js';
    this.customDataLayer = 'gtagDataLayer';

    this.listeners = this.createStandardTrackingListeners();
  }

  async init() {
    try {
      if (this.isDisabled()) {
        this.state = 'disabled';
        return this;
      }

      const token = this.getToken();

      if (!token) {
        logger.debug('No token found for GA tracker');
        this.state = 'error';
        return this;
      }

      const oldTag = document.getElementById(this.scriptTagId!);
      if (oldTag) {
        logger.debug(`${this.trackerName} already exists on the page`);
        return this;
      }

      (window as any)[`ga-disable-${token}`] = false;
      const url = this.scriptUrl + '?l=' + this.customDataLayer + '&id=' + token;
      initGa(token, this.customDataLayer);
      await loadScript({
        url,
        id: this.scriptTagId!,
        timeout: 2000,
      });
      this.state = 'ready';
      logger.debug('GA tracker initialized');
    } catch (err) {
      logger.error('GA tracker initialization error', err);
      this.cleanup();
      this.state = 'error';
      throw err;
    }

    return this;
  }

  async trackEvent(e: Event) {
    if (!this.shouldTrackEvent(e)) return;

    const mappedEvent = makeMapping(this.trackerName, 'trackEvent', normalizeEventValue(e.value, 'underscore'));
    if (!mappedEvent) {
      logger.debug('Values not found in the mapping. Event tracking aborted', e.value);
      return;
    }

    const value = mappedEvent.value as string;
    const category = value.substring(0, value.indexOf('_'));
    try {
      const eventParams = {
        debug_mode: getNinjaConfig().debug,
        eventLabel: value,
        eventCategory: category || 'default',
        ...this.getTrackParams(e),
      };
      window.gtag('event', value, eventParams);

      logger.debug('GA event tracked', {
        value: value,
        props: e.props,
        eventParams,
      });
    } catch (error) {
      logger.error('Failed to track GA event', error, e.actionType, e.value, e.props);
    }
  }

  async trackPage(e: Event) {
    if (!this.shouldTrackEvent(e)) return;

    if (!e.value) {
      logger.debug('Page name missing. Page tracking aborted');
      return;
    }

    const mappedPage = makeMapping(this.trackerName, 'trackPage', e.value);
    if (!mappedPage) {
      logger.debug('Values not found in the mapping. Page tracking aborted', e.value);
      return;
    }
    const pageName = this.convertPageNameString(decodeURIComponent(mappedPage.value));
    try {
      window.gtag('event', 'page_view', {
        page_title: pageName,
        page_path: window.location.pathname,
        ...this.getTrackParams(e),
      });
      logger.debug('GA page tracked', e.actionType, pageName, e.props);
    } catch (error) {
      logger.error('Failed to track GA page', error, e.actionType, pageName, e.props);
    }
  }

  // Disable SDK
  cleanup() {
    super.cleanup();
    (window as any)[`ga-disable-${this.getToken()}`] = true;
  }

  convertPageNameString(text: any) {
    if (typeof text !== 'string') {
      return text;
    }
    return text.replace(/[^a-zA-Z0-9/]/g, '_');
  }
}
